import React from 'react';
import { VORANK_ERLAEUTERUNG_UNTERSCHRIFT, erlaeuterungenFuer } from '../../../data/vorankErlaeuterungen';
import { NICHT_ANGEGEBEN } from '../../../services/pdf/fehlendeAngabe';
import {
  VORANK_FUNKTION_MAX,
  VORANK_KORREKTUR_MAX,
  VORANK_NAMENSTEIL_MAX,
  type VorankPublicAntwort,
} from '../../../services/vorank/vorankUnterschriftTypes';
import { SignaturePad } from '../../common/SignaturePad';
import { TeilnahmeRahmen } from '../../sifa/public/TeilnahmeRahmen';
import {
  ALS_LINK,
  EINGABE,
  FEHLERKASTEN,
  HINWEISKASTEN,
  KNOPF_PRIMAER,
  KNOPF_SEKUNDAER,
  TEXTLINK,
} from '../../sifa/public/teilnahmeSeiteStil';
import { useVorankUnterschrift, type PdfStand, type VorankUnterschriftSteuerung } from './useVorankUnterschrift';
import {
  RECHNER_WEGE,
  TEXTE,
  absenderZeilen,
  eingabeFehler,
  endAnsicht,
  fassungZeile,
  fehlendeAngaben,
  fehltHinweis,
  fertigAnsicht,
  korrekturGemeldetText,
  korrekturHinweis,
  korrekturRest,
  projektZeile,
  punktKarten,
  vorankPdfDateiname,
  type PunktKarte,
} from './vorankUnterschriftLogic';

/** Breite der zurückgegebenen Unterschrift — wie die Obergrenze der Bildaufbereitung (600 × 200). */
const UNTERSCHRIFT_MAX_BREITE = 600;

const HINWEIS_GELB = 'p-3 bg-amber-50 border border-amber-200 rounded-xl text-base text-amber-900';

/** Absender, Projekt, „Fassung vom …“. */
const Kopf: React.FC<{ daten: VorankPublicAntwort }> = ({ daten }) => {
  const absender = absenderZeilen(daten.inhalt);
  const projekt = projektZeile(daten);
  const fassung = fassungZeile(daten);
  return (
    <header className="space-y-1 pb-4 border-b border-slate-200">
      {absender.length > 0 && (
        <div className="text-sm text-slate-500 break-words">
          <p className="font-semibold text-slate-600">{absender[0]}</p>
          {absender.slice(1).map((z, i) => <p key={i}>{z}</p>)}
        </div>
      )}
      <h1 className="pt-2 text-2xl font-bold text-slate-900">Vorankündigung nach BaustellV</h1>
      {projekt && <p className="font-semibold text-slate-800 break-words">Projekt {projekt}</p>}
      {fassung && <p className="text-sm text-slate-500">{fassung}</p>}
    </header>
  );
};

/** Ein Punkt der Vorankündigung als Karte, die Erläuterung aufklappbar. */
const PunktKarteAnsicht: React.FC<{ k: PunktKarte }> = ({ k }) => (
  <li className="p-3 rounded-xl border border-slate-200 bg-white space-y-2">
    <p className="font-semibold text-slate-900 break-words">
      {k.punkt}. {k.titel}
    </p>
    {k.gruppen.map((g, gi) => (
      <div key={gi} className="space-y-1">
        {g.titel && <p className="text-sm font-semibold text-slate-600">{g.titel}</p>}
        {g.fehlt ? (
          <p className="italic text-slate-500">{NICHT_ANGEGEBEN}</p>
        ) : (
          g.eintraege.map((e, ei) => (
            <div key={ei} className="text-slate-800 break-words">
              {e.map((z, zi) => <p key={zi}>{z}</p>)}
            </div>
          ))
        )}
      </div>
    ))}
    {k.erlaeuterung && (
      <details>
        <summary className="cursor-pointer select-none py-1 text-sm font-semibold text-blue-700">{TEXTE.erlaeuterung}</summary>
        <p className="pt-1 text-sm text-slate-600">{k.erlaeuterung}</p>
      </details>
    )}
  </li>
);

/** PDF-Knopf; nach dem Erzeugen zusätzlich als Links, falls der Browser das Fenster blockiert. */
const PdfBereich: React.FC<{ pdf: PdfStand; dateiname: string; onAnsehen: () => void }> = ({ pdf, dateiname, onAnsehen }) => (
  <div className="space-y-2">
    {pdf.status === 'bereit' ? (
      <div className="flex flex-col sm:flex-row gap-2">
        <a href={pdf.url} target="_blank" rel="noopener" className={`${KNOPF_SEKUNDAER} ${ALS_LINK}`}>
          {TEXTE.pdfOeffnen}
        </a>
        <a href={pdf.url} download={dateiname} className={`${KNOPF_SEKUNDAER} ${ALS_LINK}`}>
          {TEXTE.pdfHerunterladen}
        </a>
      </div>
    ) : (
      <button type="button" onClick={onAnsehen} className={KNOPF_SEKUNDAER}>
        {TEXTE.pdfAnsehen}
      </button>
    )}
    {pdf.status === 'fehler' && <div className={FEHLERKASTEN}>{pdf.text}</div>}
  </div>
);

/** Pflicht-Häkchen mit großer Tippfläche (Muster GBU-Freigabe). */
const Haekchen: React.FC<{ checked: boolean; disabled: boolean; onChange: (wert: boolean) => void }> = ({ checked, disabled, onChange }) => (
  <label
    htmlFor="vorank-geprueft"
    className={`flex items-start gap-3 p-4 min-h-[56px] rounded-xl border-2 cursor-pointer transition-all ${
      checked ? 'border-blue-600 bg-blue-50' : 'border-slate-200 bg-white hover:border-slate-300'
    }`}
  >
    <input
      id="vorank-geprueft"
      type="checkbox"
      checked={checked}
      disabled={disabled}
      onChange={(e) => onChange(e.target.checked)}
      className="mt-0.5 w-6 h-6 shrink-0 accent-blue-600"
    />
    <span className="text-base text-slate-800 leading-snug">{TEXTE.haekchen}</span>
  </label>
);

/** Die Unterschrift so, wie sie in der PDF über der Linie steht (Feld 55 × 14 mm). */
const UnterschriftVorschau: React.FC<{ bild: string }> = ({ bild }) => (
  <div className="p-4 bg-white border border-slate-200 rounded-xl space-y-2">
    <p className="text-sm font-semibold text-slate-600">{TEXTE.vorschauTitel}</p>
    <div className="mx-auto w-full max-w-[330px]">
      <div className="flex items-end justify-center" style={{ aspectRatio: '55 / 14' }}>
        <img src={bild} alt="Ihre Unterschrift" className="max-h-full max-w-full object-contain" />
      </div>
      <div className="border-t border-slate-800" />
      <p className="pt-1 text-xs text-slate-500">{TEXTE.vorschauLinie}</p>
    </div>
  </div>
);

/** Vor- und Nachname, zwei Pflichtfelder nebeneinander (E6) — beim Unterschreiben und bei der Korrektur. */
const NamensFelder: React.FC<{
  idPraefix: string;
  vorname: string;
  nachname: string;
  disabled: boolean;
  onVorname: (wert: string) => void;
  onNachname: (wert: string) => void;
}> = ({ idPraefix, vorname, nachname, disabled, onVorname, onNachname }) => (
  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
    <div className="space-y-1.5">
      <label htmlFor={`${idPraefix}-vorname`} className="block font-semibold text-slate-800">{TEXTE.vornameLabel}</label>
      <input
        id={`${idPraefix}-vorname`}
        type="text"
        autoComplete="given-name"
        required
        maxLength={VORANK_NAMENSTEIL_MAX}
        value={vorname}
        disabled={disabled}
        onChange={(e) => onVorname(e.target.value)}
        className={EINGABE}
      />
    </div>
    <div className="space-y-1.5">
      <label htmlFor={`${idPraefix}-nachname`} className="block font-semibold text-slate-800">{TEXTE.nachnameLabel}</label>
      <input
        id={`${idPraefix}-nachname`}
        type="text"
        autoComplete="family-name"
        required
        maxLength={VORANK_NAMENSTEIL_MAX}
        value={nachname}
        disabled={disabled}
        onChange={(e) => onNachname(e.target.value)}
        className={EINGABE}
      />
    </div>
  </div>
);

/** Häkchen, Vor-/Nachname, Datum, Funktion — am Handy und auf den Wegen Maus/Bild am Rechner. */
const Angaben: React.FC<{ s: VorankUnterschriftSteuerung }> = ({ s }) => (
  <>
    <Haekchen checked={s.gelesen} disabled={s.sendet} onChange={s.setGelesen} />
    <NamensFelder
      idPraefix="vorank"
      vorname={s.vorname}
      nachname={s.nachname}
      disabled={s.sendet}
      onVorname={s.setVorname}
      onNachname={s.setNachname}
    />
    <div className="space-y-1.5">
      <label htmlFor="vorank-datum" className="block font-semibold text-slate-800">{TEXTE.datumLabel}</label>
      <input
        id="vorank-datum"
        type="date"
        required
        min={s.grenzen.min ?? undefined}
        max={s.grenzen.max}
        value={s.datum}
        disabled={s.sendet}
        onFocus={s.datumFokus}
        onChange={(e) => s.setDatum(e.target.value)}
        className={EINGABE}
      />
      <p className="text-sm text-slate-500">{TEXTE.datumHinweis}</p>
    </div>
    <div className="space-y-1.5">
      <label htmlFor="vorank-funktion" className="block font-semibold text-slate-800">{TEXTE.funktionLabel}</label>
      <input
        id="vorank-funktion"
        type="text"
        autoComplete="organization-title"
        maxLength={VORANK_FUNKTION_MAX}
        value={s.funktion}
        disabled={s.sendet}
        placeholder={TEXTE.funktionPlatzhalter}
        onChange={(e) => s.setFunktion(e.target.value)}
        className={EINGABE}
      />
    </div>
  </>
);

/** Gezeichnete Unterschrift: Knopf zum Feld bzw. Vorschau mit „löschen“. */
const Gezeichnet: React.FC<{ s: VorankUnterschriftSteuerung }> = ({ s }) =>
  s.bildArbeitet ? (
    <p className="text-slate-500" aria-live="polite">{TEXTE.unterschriftArbeitet}</p>
  ) : s.bild ? (
    <div className="space-y-2">
      <UnterschriftVorschau bild={s.bild.daten} />
      <button type="button" onClick={s.unterschriftLoeschen} disabled={s.sendet} className={TEXTLINK}>
        {TEXTE.unterschriftLoeschen}
      </button>
    </div>
  ) : (
    <button type="button" onClick={s.padOeffnen} disabled={s.sendet} className={KNOPF_SEKUNDAER}>
      {TEXTE.jetztUnterschreiben}
    </button>
  );

/** Weg 3 am Rechner: Bild wählen, aufbereiten, Vorschau — erst dann absenden. */
const BildHochladen: React.FC<{ s: VorankUnterschriftSteuerung }> = ({ s }) => (
  <div className="space-y-2">
    {s.bild && <UnterschriftVorschau bild={s.bild.daten} />}
    <label className={`${KNOPF_SEKUNDAER} ${ALS_LINK} cursor-pointer ${s.bildArbeitet || s.sendet ? 'opacity-50 pointer-events-none' : ''}`}>
      {s.bildArbeitet ? TEXTE.bildArbeitet : s.bild ? TEXTE.anderesBild : TEXTE.bildWaehlen}
      <input
        type="file"
        accept="image/png,image/jpeg"
        className="sr-only"
        disabled={s.bildArbeitet || s.sendet}
        onChange={(e) => {
          void s.bildGewaehlt(e.target.files?.[0] ?? null);
          // Dieselbe Datei darf noch einmal gewählt werden.
          e.target.value = '';
        }}
      />
    </label>
    {s.bildFehler && <div className={FEHLERKASTEN} role="alert">{s.bildFehler}</div>}
  </div>
);

/** Weg 1 am Rechner: QR auf denselben Link, die Seite fragt nach. */
const QrBereich: React.FC<{ s: VorankUnterschriftSteuerung }> = ({ s }) => (
  <div className="space-y-3">
    <h3 className="text-lg font-bold text-slate-900">{TEXTE.qrTitel}</h3>
    <p className="text-slate-600">{TEXTE.qrText}</p>
    {s.qrBild && (
      <img src={s.qrBild} alt="QR-Code zu dieser Seite" width={240} height={240} className="mx-auto w-60 h-60 border border-slate-200 rounded-xl" />
    )}
    {s.qrFehler && <div className={FEHLERKASTEN} role="alert">{s.qrFehler}</div>}
    {s.abfrageZeitUm ? (
      <div className="space-y-2">
        <div className={HINWEISKASTEN}>{TEXTE.qrZeitUm}</div>
        <button type="button" onClick={s.erneutPruefen} className={KNOPF_PRIMAER}>{TEXTE.erneutPruefen}</button>
      </div>
    ) : (
      <p className="text-sm text-slate-500" aria-live="polite">{TEXTE.qrWartet}</p>
    )}
  </div>
);

/** Hinweis, was fehlt, bzw. Fehler — und der Knopf zum Absenden. */
const Absenden: React.FC<{ s: VorankUnterschriftSteuerung }> = ({ s }) => {
  const eingabe = {
    gelesen: s.gelesen,
    vorname: s.vorname,
    nachname: s.nachname,
    funktion: s.funktion,
    datum: s.datum,
    grenzen: s.grenzen,
    bild: s.bild?.daten ?? null,
  };
  const hinweis = fehltHinweis(fehlendeAngaben(eingabe));
  const hart = eingabeFehler(eingabe);
  return (
    <>
      {(s.fehler || hart) && <div className={FEHLERKASTEN} role="alert">{s.fehler || hart}</div>}
      {hinweis && !s.fehler && !hart && <div className={HINWEISKASTEN} aria-live="polite">{hinweis}</div>}
      <button
        type="button"
        onClick={() => void s.unterschreiben()}
        disabled={s.sendet || s.bildArbeitet || !!hinweis || !!hart}
        className={KNOPF_PRIMAER}
      >
        {s.sendet ? TEXTE.sendet : TEXTE.absenden}
      </button>
    </>
  );
};

/** Unterschreiben: am Handy direkt, am Rechner über einen der drei Wege (E5). */
const UnterschriftAbschnitt: React.FC<{ s: VorankUnterschriftSteuerung }> = ({ s }) => {
  const anderer = (
    <div>
      <button type="button" onClick={s.wegZurueck} disabled={s.sendet} className={TEXTLINK}>
        {TEXTE.andererWeg}
      </button>
    </div>
  );

  let inhalt: React.ReactNode;
  if (s.geraet === 'handy') {
    inhalt = (
      <>
        <Angaben s={s} />
        <Gezeichnet s={s} />
        <Absenden s={s} />
      </>
    );
  } else if (s.weg === null) {
    inhalt = (
      <ul className="space-y-2">
        {RECHNER_WEGE.map((w) => (
          <li key={w.weg}>
            <button
              type="button"
              onClick={() => s.wegWaehlen(w.weg)}
              className="w-full text-left p-4 rounded-xl border-2 border-slate-200 bg-white hover:border-blue-400 hover:bg-blue-50 transition-all"
            >
              <span className="block font-bold text-slate-900">{w.titel}</span>
              <span className="block text-sm text-slate-600">{w.text}</span>
            </button>
          </li>
        ))}
      </ul>
    );
  } else if (s.weg === 'qr') {
    inhalt = (
      <>
        <QrBereich s={s} />
        {anderer}
      </>
    );
  } else {
    inhalt = (
      <>
        <Angaben s={s} />
        {s.weg === 'maus' ? <Gezeichnet s={s} /> : <BildHochladen s={s} />}
        <Absenden s={s} />
        {anderer}
      </>
    );
  }

  return (
    <section className="space-y-4 pt-4 border-t border-slate-200">
      <h2 className="text-lg font-bold text-slate-900">{TEXTE.unterschreibenAbschnitt}</h2>
      <p className="text-sm text-slate-600">{VORANK_ERLAEUTERUNG_UNTERSCHRIFT}</p>
      {inhalt}
    </section>
  );
};

/** „Korrektur nötig“ (E2): Vor- und Nachname (E6) + Text statt Unterschrift. */
const KorrekturAbschnitt: React.FC<{ s: VorankUnterschriftSteuerung }> = ({ s }) => {
  if (!s.korrekturOffen) {
    return (
      <section className="pt-4 border-t border-slate-200">
        <button type="button" onClick={s.korrekturOeffnen} disabled={s.sendet} className={KNOPF_SEKUNDAER}>
          {TEXTE.korrekturKnopf}
        </button>
      </section>
    );
  }
  const hinweis = korrekturHinweis({ vorname: s.korrekturVorname, nachname: s.korrekturNachname, text: s.korrekturText });
  const rest = korrekturRest(s.korrekturText);
  return (
    <section className="space-y-4 pt-4 border-t border-slate-200">
      <h2 className="text-lg font-bold text-slate-900">{TEXTE.korrekturTitel}</h2>
      <p className="text-slate-600">{TEXTE.korrekturText}</p>
      <NamensFelder
        idPraefix="vorank-korrektur"
        vorname={s.korrekturVorname}
        nachname={s.korrekturNachname}
        disabled={s.korrekturSendet}
        onVorname={s.setKorrekturVorname}
        onNachname={s.setKorrekturNachname}
      />
      <div className="space-y-1.5">
        <label htmlFor="vorank-korrektur-text" className="block font-semibold text-slate-800">{TEXTE.korrekturTextLabel}</label>
        <textarea
          id="vorank-korrektur-text"
          rows={5}
          maxLength={VORANK_KORREKTUR_MAX + 200}
          value={s.korrekturText}
          disabled={s.korrekturSendet}
          onChange={(e) => s.setKorrekturText(e.target.value)}
          className={EINGABE}
        />
        <p className={`text-sm ${rest < 0 ? 'text-red-700' : 'text-slate-500'}`}>
          {rest < 0 ? `${-rest} Zeichen zu viel` : `noch ${rest} Zeichen`}
        </p>
      </div>
      {s.korrekturFehler && <div className={FEHLERKASTEN} role="alert">{s.korrekturFehler}</div>}
      {hinweis && !s.korrekturFehler && <div className={HINWEISKASTEN} aria-live="polite">{hinweis}</div>}
      <div className="flex flex-col sm:flex-row gap-2">
        <button
          type="button"
          onClick={() => void s.korrekturSenden()}
          disabled={s.korrekturSendet || !!hinweis}
          className={KNOPF_PRIMAER}
        >
          {s.korrekturSendet ? TEXTE.sendet : TEXTE.korrekturSenden}
        </button>
        <button type="button" onClick={s.korrekturSchliessen} disabled={s.korrekturSendet} className={KNOPF_SEKUNDAER}>
          {TEXTE.korrekturAbbrechen}
        </button>
      </div>
    </section>
  );
};

/** Unterschrieben: wer/wann und der Download der unterschriebenen Vorankündigung. */
const Fertig: React.FC<{ s: VorankUnterschriftSteuerung; daten: VorankPublicAntwort }> = ({ s, daten }) => {
  const f = fertigAnsicht(daten, s.token, s.danke);
  return (
    <div className="space-y-4" aria-live="polite">
      <div className="flex items-center gap-3">
        <span aria-hidden="true" className="flex items-center justify-center w-12 h-12 rounded-full shrink-0 bg-emerald-100 text-emerald-700">
          <svg viewBox="0 0 24 24" className="w-7 h-7" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
            <path d="M5 13l4 4L19 7" />
          </svg>
        </span>
        <h2 className="text-2xl font-bold text-emerald-700">{f.titel}</h2>
      </div>
      {f.zeilen.map((z, i) => <p key={i} className="text-slate-700">{z}</p>)}
      {f.download && (
        <div className="space-y-2">
          <button type="button" onClick={s.signiertHerunterladen} className={KNOPF_PRIMAER}>
            {TEXTE.signiertHerunterladen}
          </button>
          {s.pdfSigniert.status === 'bereit' && (
            <a href={s.pdfSigniert.url} target="_blank" rel="noopener" className={`block ${TEXTLINK}`}>
              {TEXTE.pdfOeffnen}
            </a>
          )}
          {s.pdfSigniert.status === 'fehler' && <div className={FEHLERKASTEN}>{s.pdfSigniert.text}</div>}
        </div>
      )}
      {s.danke && <p className="text-sm text-slate-500">{TEXTE.fenster}</p>}
    </div>
  );
};

/** Prüfen, unterschreiben oder Korrektur melden. */
const Formular: React.FC<{ s: VorankUnterschriftSteuerung; daten: VorankPublicAntwort }> = ({ s, daten }) => {
  const karten = punktKarten(daten.inhalt, erlaeuterungenFuer(daten.inhalt));
  const korrektur = korrekturGemeldetText(s.korrekturGemeldet, daten.korrekturGemeldetAm);
  return (
    <>
      <p className="text-slate-600">{TEXTE.einleitung}</p>
      {korrektur && (
        <div className={HINWEIS_GELB} aria-live="polite">
          <p className="font-semibold">{korrektur}</p>
          <p>{TEXTE.trotzdemUnterschreiben}</p>
        </div>
      )}
      <PdfBereich pdf={s.pdf} dateiname={vorankPdfDateiname(daten, false)} onAnsehen={s.pdfAnsehen} />
      <ol className="space-y-3">
        {karten.map((k) => <PunktKarteAnsicht key={k.punkt} k={k} />)}
      </ol>
      <UnterschriftAbschnitt s={s} />
      <KorrekturAbschnitt s={s} />
    </>
  );
};

/**
 * Öffentliche Seite `#/vorankuendigung/<token>`
 * (docs/vorankuendigung-unterschrift-plan.md §6, §6.1).
 *
 * Der Bauherr braucht kein Login: er prüft die eingefrorene Fassung (je Punkt
 * eine Karte mit Erläuterung, dazu die PDF), unterschreibt — am Handy direkt,
 * am Rechner per QR aufs Handy, mit der Maus oder als hochgeladenes Bild —
 * oder meldet eine Korrektur. index.tsx routet am Auth-Gate vorbei. Dünner
 * Container: Zustand in useVorankUnterschrift, Regeln und Texte in
 * vorankUnterschriftLogic.
 */
export const VorankUnterschriftSeite: React.FC<{ token: string }> = ({ token }) => {
  const s = useVorankUnterschrift(token);

  if (s.zustand === 'laedt') {
    return (
      <TeilnahmeRahmen>
        <p className="text-slate-500" aria-live="polite">{TEXTE.laedt}</p>
      </TeilnahmeRahmen>
    );
  }

  const ende = endAnsicht(s.lade);
  if (ende || !s.daten) {
    return (
      <TeilnahmeRahmen>
        <h1 className="text-xl font-bold text-slate-900">{ende?.titel ?? 'Dieser Link ist nicht gültig'}</h1>
        {ende && <div className={ende.erneut ? FEHLERKASTEN : HINWEISKASTEN}>{ende.text}</div>}
        {ende?.erneut && (
          <button type="button" onClick={s.erneutLaden} className={KNOPF_PRIMAER}>
            {TEXTE.erneutVersuchen}
          </button>
        )}
      </TeilnahmeRahmen>
    );
  }

  // Unterschrieben wird im Vollbild — am Handy wie mit der Maus.
  if (s.padOffen && s.zustand === 'offen') {
    return (
      <SignaturePad
        alsOverlay
        titel={TEXTE.padTitel}
        hinweis={TEXTE.padHinweis}
        maxBreite={UNTERSCHRIFT_MAX_BREITE}
        uebernehmenLabel={TEXTE.unterschriftUebernehmen}
        onUebernehmen={(dataUrl) => void s.unterschriftUebernehmen(dataUrl)}
        onAbbrechen={s.padSchliessen}
        abbrechenLabel="Zurück"
      />
    );
  }

  return (
    <TeilnahmeRahmen>
      <Kopf daten={s.daten} />
      {s.zustand === 'unterschrieben' ? <Fertig s={s} daten={s.daten} /> : <Formular s={s} daten={s.daten} />}
    </TeilnahmeRahmen>
  );
};

export default VorankUnterschriftSeite;
